import { useCallback, useMemo, useState } from "react"
import ReactPlayer from "react-player"
import {
  aCurrentVideoQuality,
  aIsQualityOpen,
  aVideoQuality,
  aVideoQualityLevelHeights,
  useAtom,
  useAtomValue,
} from "~/atoms"
import { Tooltip } from "~/components/ui"
import useIsPhone from "~/hooks/useIsPhone"
import { cn } from "~/utils"
import { HdIcon } from "../icons"
import { ControlButton } from "./control-button"
import ControlPopoverContent from "./control-popover-content"

export default function QualityControl({
  className,
  videoRef,
}: {
  className?: string
  videoRef: React.RefObject<ReactPlayer>
}) {
  const [isQualityOpen, setIsQualityOpen] = useAtom(aIsQualityOpen)
  const [videoQuality, setVideoQuality] = useAtom(aVideoQuality)
  const videoQualityLevelHeights = useAtomValue(aVideoQualityLevelHeights)
  const currentVideoQuality = useAtomValue(aCurrentVideoQuality)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const isPhone = useIsPhone()

  const options = useMemo(() => {
    return [
      ...videoQualityLevelHeights.map((height) => ({
        label: `${height}p`,
        selected: videoQuality === height,
        value: height,
      })),
      {
        label: "Auto",
        selected: videoQuality === -1,
        value: -1,
      },
    ]
  }, [videoQualityLevelHeights, videoQuality])

  const updateVideoQuality = useCallback(
    (height: number) => {
      setIsQualityOpen(false)
      setVideoQuality(height)
      const internalPlayer = videoRef.current?.getInternalPlayer("hls")
      if (internalPlayer) {
        internalPlayer.currentLevel = videoQualityLevelHeights.findIndex(
          (h) => h === height
        )
      }
    },
    [setIsQualityOpen, setVideoQuality, videoRef, videoQualityLevelHeights]
  )

  if (options.length === 1) return null

  return (
    <div className="relative flex items-center">
      <Tooltip.Provider>
        <Tooltip delayDuration={200} open={isTooltipOpen}>
          <Tooltip.Trigger asChild>
            <ControlButton
              className={cn(className, isPhone && "!h-8 !px-3")}
              aria-label={
                videoQuality === -1
                  ? currentVideoQuality === -1
                    ? "Video quality: Auto"
                    : `Video quality: Auto (${currentVideoQuality}p)`
                  : `Video quality: ${videoQuality}p`
              }
              aria-expanded={isQualityOpen}
              aria-haspopup="menu"
              onClick={(e) => {
                e.stopPropagation()
                setIsQualityOpen(!isQualityOpen)
                setIsTooltipOpen(false)
              }}
              onMouseOver={() => { if (!isQualityOpen) setIsTooltipOpen(true) }}
              onMouseLeave={() => setIsTooltipOpen(false)}
              onFocus={() => { if (!isQualityOpen) setIsTooltipOpen(true) }}
              onBlur={() => setIsTooltipOpen(false)}
            >
              <HdIcon className="h-6 w-6 text-white sm:h-7 sm:w-7" />
            </ControlButton>
          </Tooltip.Trigger>
          <Tooltip.Content>
            {videoQuality === -1
              ? currentVideoQuality === -1
                ? "Auto"
                : `Auto (${currentVideoQuality}p)`
              : `${videoQuality}p`}
          </Tooltip.Content>
        </Tooltip>
      </Tooltip.Provider>
      <ControlPopoverContent<number>
        title="Video Quality"
        isOpen={isQualityOpen}
        onClose={() => setIsQualityOpen(false)}
        options={options}
        onClick={updateVideoQuality}
      />
    </div>
  )
}
